import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import Button from 'components/App/Button';
import Card from 'components/App/Card';
import Breadcrumb from 'components/App/Breadcrumb';
import Loading from 'components/App/Loading';

import { LOCATIONS } from 'constants/index';
import cn from 'utils/classnames';
import formatDate from 'utils/formatDate';
import INITIAL_VALUE from 'constants/initialValue';

import { ReactComponent as BasicPromotionIcon } from 'assets/images/basic-promotion.svg';
import { ReactComponent as LastMinutePromotionIcon } from 'assets/images/last-minute-promotion.svg';
import { ReactComponent as EarlyBookingPromotionIcon } from 'assets/images/early-booking-promotion.svg';
import { ReactComponent as LongTermPromotionIcon } from 'assets/images/long-term-promotion.svg';
// import { ReactComponent as PhonePromotionIcon } from 'assets/images/phone-promotion.svg';
// import { ReactComponent as MembershipPromotionIcon } from 'assets/images/
// membership-promotion.svg';

import { ReactComponent as GuestPromotionIcon } from 'assets/images/guest-promotion.svg';
import { ReactComponent as BusinessPromotionIcon } from 'assets/images/business-promotion.svg';
import { ReactComponent as AgencyPromotionIcon } from 'assets/images/agency-promotion.svg';
import { ReactComponent as RankPromotionIcon } from 'assets/images/rank-promotion.svg';
import { ReactComponent as MarketingPromotionIcon } from 'assets/images/marketing-promotion-1.svg';

import styles from './CreatePromotion.module.scss';

const formatBookingDateRange = (bookingDate) => {
  if (!bookingDate?.from || !bookingDate?.to) {
    return '';
  }

  return `${formatDate(bookingDate.from)} - ${formatDate(bookingDate.to)}`;
};

const CUSTOMER_PROMOTION_LIST = [
  {
    icon: <GuestPromotionIcon />,
    name: 'Ưu đãi du khách',
    // suggest: 'Gợi ý giảm giá 10% hoặc hơn',
    type: 'Luôn hoạt động',
    desc: 'Tiếp cận khách hàng thành viên thường xuyên đặt phòng',
    link: LOCATIONS.LOCATION_DETAIL.CREATE_TOURIST_PROMOTION,
  },
  {
    icon: <AgencyPromotionIcon />,
    name: 'Ưu đãi đại lý',
    type: 'Luôn hoạt động',
    desc: 'Tiếp cận khách hàng đối tác, cộng tác viên',
    link: LOCATIONS.LOCATION_DETAIL.CREATE_AGENCY_PROMOTION,
  },
  {
    icon: <BusinessPromotionIcon />,
    name: 'Ưu đãi đại sứ',
    type: 'Luôn hoạt động',
    desc: 'Tiếp cận khách hàng thành viên từ các đại sứ',
    link: LOCATIONS.LOCATION_DETAIL.CREATE_ENTERPRISE_PROMOTION,
  },
];

const LIMIT_PROMOTION_LIST = [
  {
    icon: <RankPromotionIcon />,
    name: 'Ưu Đãi Thứ Hạng',
    // suggest: 'Gợi ý giảm giá 10% hoặc hơn',
    type: '2 tuần áp dụng 1 lần',
    desc: 'Tăng thứ hạng của bạn vào top 20 thị trường',
    link: LOCATIONS.LOCATION_DETAIL.CREATE_RANK_PROMOTION,
  },
];

const POPULAR_PROMOTION_LIST = [
  {
    icon: <BasicPromotionIcon />,
    name: 'Ưu Đãi Cơ Bản',
    // suggest: 'Gợi ý giảm giá 10% hoặc hơn',
    type: 'Bất cứ ngày nào',
    desc: 'Tùy chỉnh ưu đãi theo nhu cầu của bạn',
    link: LOCATIONS.LOCATION_DETAIL.CREATE_BASIC_PROMOTION,
  },
  {
    icon: <LastMinutePromotionIcon />,
    name: 'Ưu Đãi Phút Chót',
    // suggest: 'Gợi ý giảm giá 10% hoặc hơn',
    type: 'Bất cứ ngày nào',
    desc: 'Giúp bạn lấp phòng trống còn lại',
    link: LOCATIONS.LOCATION_DETAIL.CREATE_LAST_MINUTE_PROMOTION,
  },
  {
    icon: <EarlyBookingPromotionIcon />,
    name: 'Ưu Đãi Đặt Sớm',
    // suggest: 'Gợi ý giảm giá 10% hoặc hơn',
    type: 'Bất cứ ngày nào',
    desc: 'Giúp bạn thu hút khách hàng có kế hoạch đặt phòng sớm',
    link: LOCATIONS.LOCATION_DETAIL.CREATE_EARLY_PROMOTION,
  },
  {
    icon: <LongTermPromotionIcon />,
    name: 'Ưu Đãi Dài Hạn',
    // suggest: 'Gợi ý giảm giá 10% hoặc hơn',
    type: 'Bất cứ ngày nào',
    desc: 'Giúp bạn tiếp cận khách hàng có nhu cầu lưu trú dài hạn',
    link: LOCATIONS.LOCATION_DETAIL.CREATE_LONG_TERM_PROMOTION,
  },
];

function CreatePromotion(props) {
  const {
    isLoadingGetPlaceDetail,
    isLoadingGetUsableMarketingPromotionList,
    placeDetail,
    usableMarketingPromotionList,
    actionGetPlaceDetail,
    actionResetPlaceDetail,
    actionGetUsableMarketingPromotionList,
  } = props;

  const { search } = useLocation();

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    return {
      place: params.get('place'),
    };
  }, [search]);

  const breadcrumbList = React.useMemo(() => [
    {
      name: 'Chương trình khuyến mãi',
      isActive: false,
      link: searchQuery.place
        ? `${LOCATIONS.LOCATION_DETAIL.PROMOTION}?place=${searchQuery.place}`
        : LOCATIONS.LOCATION_DETAIL.PROMOTION,
    },
    {
      name: 'Tạo chương trình khuyến mãi',
      isActive: true,
      link: '',
    },
  ], [searchQuery.place]);

  React.useEffect(() => {
    if (searchQuery.place) {
      actionGetPlaceDetail({
        placeId: searchQuery.place,
      });

      actionGetUsableMarketingPromotionList({
        placeId: searchQuery.place,
      });
    }

    return () => {
      actionResetPlaceDetail();
    };
  }, [
    actionGetPlaceDetail,
    actionGetUsableMarketingPromotionList,
    actionResetPlaceDetail,
    searchQuery.place,
  ]);

  return (
    <div className={cn('page-content', styles.Page)}>
      <Loading
        loaderOnPage
        loading={isLoadingGetPlaceDetail || isLoadingGetUsableMarketingPromotionList}
      />
      <Breadcrumb pageTitle="Chọn khuyến mãi mới" breadcrumbList={breadcrumbList} />

      <Card className={styles.Section}>
        <div className={styles.Title}>
          <h3>Ưu đãi phổ biến</h3>

          <p>
            Cải thiện tình trạng lấp đầy phòng trống với một loạt các loại ưu
            đãi có thể được tùy chỉnh cho phù hợp với nhu cầu của Bạn.
          </p>
        </div>

        {POPULAR_PROMOTION_LIST.map((item) => (
          <div className={styles.Promotion} key={item.name}>
            <div className={styles.IconWrapper}>
              {item.icon}
              <div className={styles.Title}>
                <h3>{item.name}</h3>
                <p className={styles.Strong}>{item.suggest}</p>
              </div>
            </div>

            <span>{item.type}</span>

            <span className={styles.Desc}>{item.desc}</span>

            <Link
              to={{
                pathname: item.link,
                search: `?place=${searchQuery.place}`,
              }}
            >
              <Button outline color="light" classes={styles.Button}>
                <i className="fas fa-plus" />
                Tạo
              </Button>
            </Link>
          </div>
        ))}
      </Card>
      <Card className={styles.Section}>
        <div className={styles.Title}>
          <h3>Ưu đãi tài khoản</h3>

          <p>
            Giảm giá cho các nhóm khách cụ thể và trở thành chỗ nghỉ được ưa
            thích bởi những nhóm khách hàng giá trị cao.
          </p>
        </div>

        {CUSTOMER_PROMOTION_LIST.map((item) => (
          <div className={styles.Promotion} key={item.name}>
            <div className={styles.IconWrapper}>
              {item.icon}
              <div className={styles.Title}>
                <h3>{item.name}</h3>
                <p className={styles.Strong}>{item.suggest}</p>
              </div>
            </div>

            <span>{item.type}</span>

            <span className={styles.Desc}>{item.desc}</span>

            <Link
              to={{
                pathname: item.link,
                search: `?place=${searchQuery.place}`,
              }}
            >
              <Button outline color="light" classes={styles.Button}>
                <i className="fas fa-plus" />
                Tạo
              </Button>
            </Link>
          </div>
        ))}
      </Card>
      <Card className={styles.Section}>
        <div className={styles.Title}>
          <h3>Ưu đãi giới hạn</h3>

          {/* <p>
            Giảm giá cho các nhóm khách cụ thể và trở thành chỗ nghỉ được ưa
            thích bởi những nhóm khách hàng giá trị cao.
          </p> */}
        </div>

        {LIMIT_PROMOTION_LIST.map((item) => {
          const isRankPromotion = item.link === LOCATIONS.LOCATION_DETAIL.CREATE_RANK_PROMOTION;
          const isDisabled = isRankPromotion && (placeDetail?.bookingReviewCount || 0) < 5;

          return (
            <div className={styles.Promotion} key={item.name}>
              <div className={styles.IconWrapper}>
                {item.icon}
                <div className={styles.Title}>
                  <h3>{item.name}</h3>
                  <p className={styles.Strong}>{item.suggest}</p>
                </div>
              </div>

              <span>{item.type}</span>

              <span className={styles.Desc}>{item.desc}</span>

              {isDisabled ? (
                <Button outline color="light" classes={styles.Button} disabled>
                  <i className="fas fa-plus" />
                  Tạo
                </Button>
              ) : (
                <Link
                  to={{
                    pathname: item.link,
                    search: `?place=${searchQuery.place}`,
                  }}
                >
                  <Button outline color="light" classes={styles.Button}>
                    <i className="fas fa-plus" />
                    Tạo
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
      </Card>

      <Card className={styles.Section}>
        <div className={styles.Title}>
          <h3>Ưu đãi tiếp thị</h3>
        </div>

        {usableMarketingPromotionList.map((item) => (
          <div className={styles.Promotion} key={item._id}>
            <div className={styles.IconWrapper}>
              <MarketingPromotionIcon />
              <div className={styles.Title}>
                <h3>{item.name}</h3>
              </div>
            </div>

            <span>{formatBookingDateRange(item.bookingDate)}</span>

            <span className={styles.Desc}>
              Tăng hiển thị của bạn trên các nền tảng
              truyền thông của chúng tôi
            </span>

            {item.isApplied ? (
              <Button outline color="light" classes={styles.Button} disabled>
                <i className="fas fa-plus" />
                Tham gia
              </Button>
            ) : (
              <Link
                to={{
                  pathname: LOCATIONS.LOCATION_DETAIL.CREATE_MARKETING_PROMOTION,
                  search: `?place=${searchQuery.place}&marketingPromotion=${item._id}`,
                }}
              >
                <Button outline color="light" classes={styles.Button}>
                  <i className="fas fa-plus" />
                  Tham gia
                </Button>
              </Link>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}

CreatePromotion.propTypes = {
  isLoadingGetPlaceDetail: PropTypes.bool,
  isLoadingGetUsableMarketingPromotionList: PropTypes.bool,
  placeDetail: PropTypes.instanceOf(Object),
  usableMarketingPromotionList: PropTypes.instanceOf(Array),
  actionGetPlaceDetail: PropTypes.func,
  actionResetPlaceDetail: PropTypes.func,
  actionGetUsableMarketingPromotionList: PropTypes.func,
};

CreatePromotion.defaultProps = {
  isLoadingGetPlaceDetail: false,
  isLoadingGetUsableMarketingPromotionList: false,
  placeDetail: INITIAL_VALUE.OBJECT,
  usableMarketingPromotionList: [],
  actionGetPlaceDetail: INITIAL_VALUE.FUNCTION,
  actionResetPlaceDetail: INITIAL_VALUE.FUNCTION,
  actionGetUsableMarketingPromotionList: INITIAL_VALUE.FUNCTION,
};

export default CreatePromotion;
